/* globals window, process */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import GoogleMap from 'google-map-react';
import { intlShape, FormattedMessage } from 'react-intl';
import { routerShape, locationShape } from 'react-router/lib/PropTypes';
import { autobind } from 'core-decorators';

import { disableTouchScroll } from 'utils';
import configData from 'config/data';

import MapContainer from 'AppComponents/MapContainer';
import Alert from 'AppComponents/Alert';
import ChargerMarker from 'AppComponents/MapMarker/Charger';
import PlaceMarker from 'AppComponents/MapMarker/Place';
import Loader from 'AppComponents/Loader';
import { Checkbox } from 'AppComponents/Forms';

import Place from './Place';

import './styles.scss';


class Places extends Component {

  static propTypes = {
    charger: PropTypes.shape({
      pk: PropTypes.number,
    }).isRequired,
    chargerPlacesRequest: PropTypes.func.isRequired,
    location: locationShape.isRequired,
    showCategories: PropTypes.arrayOf(PropTypes.string),
    setChargerShowPlaceCategories: PropTypes.func.isRequired,
    mobileShowMap: PropTypes.bool,
    setMobileShowMap: PropTypes.func.isRequired,
    placesById: PropTypes.shape({
      [PropTypes.string]: PropTypes.object,
    }).isRequired,
  };

  static defaultProps = {
    showCategories: [],
    mobileShowMap: false,
  };

  static contextTypes = {
    router: routerShape.isRequired,
    intl: intlShape.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    const { charger } = props;
    let chargerCoords = [];
    if (charger) {
      if (charger.custom_coords) chargerCoords = charger.custom_coords.coordinates;
      else chargerCoords = charger.address.coords.coordinates;
    }
    this.state = {
      mobileMapLoaded: false,
      map: {
        center: {
          lat: chargerCoords[1] || 0,
          lng: chargerCoords[0] || 0,
        },
        zoom: 17,
        maxZoom: null,
        maxFitZoom: 18,
        scrollwheel: false,
      },
      filteredPlaces: charger ? charger.places || [] : [],
      hoveredPlace: null,
      mobileShowCategories: false,
    };
    this._gettingBounds = false;
    this._changingBounds = false;
  }

  componentDidMount() {
    this.init();
    window.addEventListener('resize', this.handleResize);
  }

  componentWillReceiveProps(nextProps) {
    const { charger: prevCharger } = this.props;
    const { charger } = nextProps;
    if (charger.pk !== prevCharger.pk) {
      this.init(nextProps);
    }

    if (charger) {
      let chargerCoords = charger.address.coords.coordinates;
      if (charger.custom_coords) chargerCoords = charger.custom_coords.coordinates;
      const markers = [{
        props: {
          lat: chargerCoords[1],
          lng: chargerCoords[0],
        },
      }];
      if (charger.places) {
        charger.places.forEach((id) => {
          const place = nextProps.placesById[id];
          if (place.address) {
            if (place.address.coords) {
              markers.push({
                props: {
                  lat: place.address.coords.coordinates[1],
                  lng: place.address.coords.coordinates[0],
                },
              });
            }
          }
        });
      }
      this.getBoundsFromMarkers(markers);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  @autobind
  onGoogleLoaded({ map, maps }) {
    this.map = map;
    this.maps = maps;
    this.map.setMapTypeId(maps.MapTypeId.HYBRID);
    this.map.setTilt(0);
    this.getBoundsFromMarkers(this.markers);

    disableTouchScroll('map-container');
  }

  getBoundsFromMarkers(markers) {
    if (!this._changingBounds) {
      this._gettingBounds = true;
      if (this.maps) {
        const bounds = new this.maps.LatLngBounds();
        markers.forEach((marker) => {
          const point = new this.maps.LatLng(marker.props.lat, marker.props.lng);
          bounds.extend(point);
        });
        this.map.setOptions({ maxZoom: this.state.maxFitZoom });
        this.map.fitBounds(bounds);
        this.setState({
          map: Object.assign({}, this.state.map, {
            center: this.map.getCenter().toJSON(),
            zoom: this.map.getZoom(),
          }),
        });
        this.map.setOptions({ maxZoom: this.state.maxZoom });
      }
      this._gettingBounds = false;
    }
  }

  @autobind
  setHoveredPlace(id) {
    this.setState({ hoveredPlace: id });
  }

  @autobind
  handlePlaceMarkerClick(id) {
    const place = this.props.placesById[id];
    this.context.router.push(`/${place.category}/${place.pk}/`);
  }

  @autobind
  handleMapBoundsChange({ center, zoom }) {
    this._changingBounds = true;
    this.setState({
      map: Object.assign({}, this.state.map, {
        center,
        zoom,
      }),
    });
    this._changingBounds = false;
  }

  init(props) {
    const { charger, showCategories, chargerPlacesRequest } = props || this.props;
    const unloadedCategories = showCategories.filter(cat => (
      !(charger.loadedCategories && charger.loadedCategories[cat])
    ));
    if (unloadedCategories.length > 0) {
      chargerPlacesRequest(charger.pk, {
        include_simple_categories: unloadedCategories,
      });
    }
  }

  @autobind
  removeHoveredPlace() {
    this.setState({ hoveredPlace: null });
  }

  @autobind
  mobileShowPlaces() {
    window.scrollTo(0, 0);
    this.context.router.push({
      pathname: this.props.location.pathname,
      hash: '#places',
    });
  }

  @autobind
  mobileClosePlaces() {
    window.scrollTo(0, 0);
    this.context.router.push(this.props.location.pathname);
  }

  @autobind
  mobileToggleMap() {
    if (this.props.mobileShowMap) this.mobileShowList();
    else this.mobileShowMap();
  }

  @autobind
  mobileShowMap() {
    this.props.setMobileShowMap(true);
    this.setState({ mobileMapLoaded: true });
  }

  mobileShowList() {
    this.props.setMobileShowMap(false);
  }

  @autobind
  handleResize() {
    this.setState({ mobileMapLoaded: false });
  }

  @autobind
  handleMapClick() {
    this.setState({
      map: Object.assign({}, this.state.map, {
        scrollwheel: !this.state.map.scrollwheel,
      }),
    }, () => {
      this.map.setOptions({ scrollwheel: this.state.map.scrollwheel });
    });
  }

  handleCategoryClick(category, checked) {
    const cats = this.props.showCategories.slice(0);
    const index = cats.indexOf(category);
    if (checked && index === -1) {
      cats.push(category);
    } else if (!checked && index !== -1) {
      cats.splice(index, 1);
    }
    this.props.setChargerShowPlaceCategories(cats);
    const { charger } = this.props;
    if (checked && !(charger.loadedCategories && charger.loadedCategories[category])) {
      this.props.chargerPlacesRequest(charger.pk, {
        include_simple_categories: [category],
      });
    }
  }

  render() {
    const { charger } = this.props;
    const { mobileShowCategories } = this.state;

    const { loadingPlaces: loading, last_factual_update: lastUpdate } = charger;

    let chargerCoords = charger.address.coords.coordinates;
    if (charger.custom_coords) chargerCoords = charger.custom_coords.coordinates;

    const placeMarkers = [];
    const allPlaces = charger.places || [];
    const filteredPlaces = allPlaces.filter((placeId) => {
      const place = this.props.placesById[placeId];
      return this.props.showCategories.indexOf(place.category) !== -1;
    });
    filteredPlaces.sort((a, b) => {
      const aPlace = this.props.placesById[a];
      const bPlace = this.props.placesById[b];
      if (aPlace.average_rating > bPlace.average_rating) return -1;
      if (aPlace.average_rating < bPlace.average_rating) return 1;
      if (aPlace.average_rating === bPlace.average_rating) {
        if (aPlace.distance_from_charger < bPlace.distance_from_charger) return -1;
        if (aPlace.distance_from_charger > bPlace.distance_from_charger) return 1;
        if (aPlace.distance_from_charger === bPlace.distance_from_charger) return 0;
      }
      return 0;
    });
    const placeObjs = filteredPlaces.map((placeId) => {
      const place = this.props.placesById[placeId];
      if (place.address) {
        if (place.address.coords) {
          placeMarkers.push(
            <PlaceMarker
              place={place}
              key={place.pk}
              lat={place.address.coords.coordinates[1]}
              lng={place.address.coords.coordinates[0]}
              listHovered={this.state.hoveredPlace === place.pk}
              onClick={this.handlePlaceMarkerClick}
              onMouseEnter={() => this.setHoveredPlace(place.pk)}
              onMouseLeave={() => this.removeHoveredPlace(place.pk)}
              intl={this.context.intl}
            />,
          );
        }
      }
      return place;
    });

    const chargerMarker = (
      <ChargerMarker
        charger={charger}
        lat={chargerCoords[1]}
        lng={chargerCoords[0]}
      />
    );

    this.markers = placeMarkers.slice(0);
    this.markers.push(chargerMarker);

    const mapOptions = {
      scrollwheel: this.state.map.scrollwheel,
    };
    if (this.state.map.maxZoom) mapOptions.maxZoom = this.state.map.maxZoom;
    if (this.state.map.minZoom) mapOptions.minZoom = this.state.map.minZoom;

    return (
      <section className={classNames('places', { loading })} styleName='places'>

        <h2>Nearby Places</h2>

        <Alert
          type='warning'
          display={loading === true && lastUpdate === null}
        >
          It looks like we haven&#8217;t collected a list of places near this charger, yet,
          so this load will take a little longer than usual. Thanks for your patience –
          this will only happen once.
        </Alert>

        <div className={classNames('categories', { in: mobileShowCategories })} styleName='categories'>
          { Object.keys(configData.genericPlaceCategories).map((slug) => {
            const cat = configData.genericPlaceCategories[slug];
            return (
              <Checkbox
                key={slug}
                name={slug}
                label={cat.labelPlural}
                onChange={e => this.handleCategoryClick(slug, e)}
                value={this.props.showCategories.indexOf(slug) !== -1}
              />
            );
          }) }
        </div>

        <div key='loader' styleName='loading'><Loader /></div>

        <button
          className='btn btn-primary'
          styleName='show-places'
          onClick={this.mobileShowPlaces}
        >
          { loading ?
            'Loading...'
          :
            <FormattedMessage
              id='chargerDetail.placesNearby'
              values={{
                numPlaces: allPlaces.length,
              }}
              defaultMessage='{numPlaces, plural,
                zero {No Places}
                one {{numPlaces} Place}
                other {{numPlaces} Places}
              } Nearby'
            />
          }
        </button>

        <div className='navbar navbar-inverse bg-primary' styleName='mobile-nav'>

          <div className='btns-left' styleName='btns'>
            <button
              className='navbar-text'
              styleName='back'
              onClick={this.mobileClosePlaces}
            >
              Back
            </button>
          </div>

          <h2>Places</h2>

          <div className='btns-right' styleName='btns'>
            <button
              className={classNames('navbar-text', { active: mobileShowCategories })}
              styleName='filters'
              onClick={() => this.setState({ mobileShowCategories: !mobileShowCategories })}
            >
              Filters
            </button>

            <button
              className='toggle-map navbar-text'
              onClick={this.mobileToggleMap}
            >
              { this.props.mobileShowMap ? 'List' : 'Map' }
            </button>
          </div>

        </div>

        <div styleName='places-wrapper'>

          <div styleName='list-wrapper'>

            <div className='list-group' styleName='list'>

              { placeObjs.map(place => (
                <Place
                  place={place}
                  key={place.pk}
                  onMouseEnter={() => this.setHoveredPlace(place.pk)}
                  onMouseLeave={() => this.removeHoveredPlace(place.pk)}
                />
              )) }

              <div key='loader' styleName='loading'><Loader /></div>

            </div>

          </div>

          <MapContainer
            animatePlaceHover
            scrollwheel={this.state.map.scrollwheel}
            styleName='map-container'
          >
            <GoogleMap
              key={this.state.mobileMapLoaded ? 'a' : 'b'}
              bootstrapURLKeys={{ key: process.env.GOOGLE_MAPS_API_KEY }}
              center={this.state.map.center}
              zoom={this.state.map.zoom}
              options={mapOptions}
              onClick={this.handleMapClick}
              onChange={this.handleMapBoundsChange}
              onGoogleApiLoaded={this.onGoogleLoaded}
              yesIWantToUseGoogleMapApiInternals
            >
              { chargerMarker }
              { placeMarkers }
            </GoogleMap>
          </MapContainer>
        </div>

      </section>
    );
  }
}

export default Places;



// WEBPACK FOOTER //
// ./app/scripts/views/App/views/Charger/components/Places/index.jsx